import type { Application } from '@/types/database.types';
import { supabase } from '@/integrations/supabase/client';
import { VALID_FIELDS } from './save-constants';
import type { Json } from '@/integrations/supabase/types';
import { ensureStringArray, ensureDocumentArray } from '@/utils/application-data-utils';

// List of integer fields that need special handling for empty strings
const INTEGER_FIELDS = [
  'w2_employee_count',
  'exempt_employee_count',
  'non_exempt_employee_count',
  'total_1099_count',
  'k1_count',
  'annual_volunteer_count',
  'peak_w2_employee_count',
  'peak_exempt_employee_count',
  'peak_non_exempt_employee_count',
  'non_peak_w2_employee_count',
  'non_peak_exempt_employee_count',
  'non_peak_non_exempt_employee_count',
  'employees_enrolled_in_health_plan',
  'employee_only_count',
  'employee_spouse_count',
  'employee_children_count',
  'employee_family_count',
  'active_pregnancies_count',
  'annual_foreign_trips',
  'avg_trip_duration',
  'max_employees_per_trip',
  'layoff_percentage',
  'layoff_releases_percentage',
  'layoff_count',
  'planned_layoff_count',
  'voluntary_terminations',
  'involuntary_terminations',
  'progress'
];

// Helper to filter out placeholder values for select fields and handle integer fields
export const normalizeFormData = (data: Partial<Record<string, any>>) => {
  const result = { ...data };
  
  // List of placeholder values that should be converted to null
  const placeholderValues = [
    'select_state',
    'select_industry',
    'select_provider',
    'select_structure'
  ];
  
  // Convert placeholder values to null and handle integer fields
  Object.keys(result).forEach(key => {
    // Handle placeholder values
    if (typeof result[key] === 'string' && placeholderValues.includes(result[key])) {
      result[key] = null;
    }
    
    // Handle integer fields - convert empty strings to null
    if (INTEGER_FIELDS.includes(key)) {
      if (result[key] === '' || result[key] === undefined) {
        result[key] = null;
      } else if (typeof result[key] === 'string' && result[key] !== null) {
        // Try to parse as integer, if it fails, set to null
        const parsed = parseInt(result[key], 10);
        result[key] = isNaN(parsed) ? null : parsed;
      } else if (typeof result[key] === 'number' && isNaN(result[key])) {
        result[key] = null;
      }
    }
  });
  
  return result;
};

export const prepareUpdateData = (
  updateData: Partial<Record<string, any>>,
  existingData: Record<string, any>,
  type: 'application'
) => {
  console.log('Preparing update data for', type, updateData);
  
  // First normalize any placeholder values
  const normalizedData = normalizeFormData(updateData);

  const changes = Object.entries(normalizedData).filter(([key, value]) => {
    // Special handling for array and object fields that are stored as JSON
    if (key === 'additional_contacts' || key === 'health_plans') {
      return JSON.stringify(value) !== JSON.stringify(existingData[key]);
    }
    
    // Special handling for additional_schedules which is stored as jsonb[]
    if (key === 'additional_schedules') {
      // Deep comparison for array of objects
      return JSON.stringify(value) !== JSON.stringify(existingData[key]);
    }
    
    return value !== existingData[key];
  });

  const update = Object.fromEntries(changes);
  console.log('Changes detected:', update);
  return update;
};

/**
 * Converts application data to a format suitable for database storage
 * This ensures complex objects like AdditionalContact[] are converted to Json
 */
export const convertToDbFormat = (data: Partial<Application>): Record<string, any> => {
  const result: Record<string, any> = {};
  
  Object.entries(data).forEach(([key, value]) => {
    // Handle arrays of objects that need to be stored as JSONB
    if (key === 'additional_contacts' || key === 'health_plans') {
      // Ensure we're sending proper JSONB for these fields
      result[key] = value as Json;
    }
    // Handle special case for additional_schedules which is stored as jsonb[]
    else if (key === 'additional_schedules') {
      // Ensure it's formatted as an array for the database
      result[key] = value as Json[];
    }
    // Handle document fields that should be stored as strings but are arrays in the interface
    else if (['employee_census_file', 'dependent_census_file', 'workers_comp_declaration_file', 
              'workers_comp_loss_runs_file', 'health_insurance_renewal_file', 'sbc_file', 
              'tax_exempt_status_file', 'other_documents'].includes(key)) {
      // Convert arrays back to JSON strings for database storage
      if (Array.isArray(value)) {
        result[key] = value.length > 0 ? JSON.stringify(value) : null;
      } else {
        result[key] = value;
      }
    }
    // Handle array fields that are stored as JSON strings in the database
    else if (['desired_medical_carriers', 'desired_ancillary_carriers', 'states_of_operation',
              'states_with_employees', 'top_3_peo_needs', 'preferred_peos', 'excluded_peos'].includes(key)) {
      // Convert arrays to JSON strings for database storage
      if (Array.isArray(value)) {
        result[key] = value.length > 0 ? JSON.stringify(value) : null;
      } else {
        result[key] = value;
      }
    }
    else {
      result[key] = value;
    }
  });
  
  return result;
};

/**
 * Prepares data for saving to the database, ensuring it's in the correct format
 * @param application Current application state
 * @param updates New data to be saved
 * @returns Properly formatted data for database update
 */
export const prepareApplicationDataForSave = (
  application: Application,
  updates: Partial<Application>
): Record<string, any> => {
  // First normalize the data to convert placeholder values to null
  const normalizedData = normalizeFormData(updates);
  
  // Process special cases for specific fields
  const processedUpdates = { ...normalizedData };
  
  // Apply database-friendly changes to the update data
  const updatesToSave = prepareUpdateData(processedUpdates, application, 'application');
  
  // Make sure we're only saving valid fields to the database
  const validUpdates: Record<string, any> = {};
  Object.keys(updatesToSave).forEach(key => {
    if (VALID_FIELDS.includes(key)) {
      validUpdates[key] = updatesToSave[key];
    } else {
      console.warn(`Field ${key} is not in the allowed list of fields and will not be saved.`);
    }
  });
  
  // Convert to database-compatible format
  return convertToDbFormat(validUpdates);
};

export const saveApplicationData = async (
  application: Application | null,
  setApplication: (app: Application | null) => void,
  updates: Partial<Application>,
  calculateProgress: () => number
): Promise<void> => {
  if (!application) {
    console.error('No application to update.');
    return;
  }

  const preparedUpdates = prepareApplicationDataForSave(application, updates);

  if (Object.keys(preparedUpdates).length === 0) {
    console.log('No changes detected, skipping save.');
    return;
  }

  try {
    const { data, error } = await supabase
      .from('applications')
      .update(preparedUpdates)
      .eq('id', application.id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    // Optimistically update the local state
    setApplication({
      ...application,
      ...updates, // Use original updates to maintain proper types in the React state
      progress: calculateProgress(),
      updated_at: new Date().toISOString(),
    });

    console.log('Application updated successfully:', data);
  } catch (error) {
    console.error('Error updating application:', error);
    throw error;
  }
};
